import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Heart, Calendar, MapPin, Phone, Mail, Edit, Droplets, Activity, Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    bloodGroup: '',
    interests: []
  });

  // Debounced load donor status
  const loadDonorStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/blood-donors/status');
      if (response.data.isRegistered) {
        updateUser({
          ...user,
          isBloodDonor: true,
          donorAvailable: response.data.isAvailable
        });
      }
    } catch (error) {
      console.error('Error loading donor status:', error);
    }
  }, [user, updateUser]);

  // Load donor status on component mount with debounce
  useEffect(() => {
    if (user) {
      const timeoutId = setTimeout(() => {
        loadDonorStatus();
      }, 2000); // Wait 2 seconds before making the request

      return () => clearTimeout(timeoutId);
    }
  }, [user, loadDonorStatus]);

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        bloodGroup: user.bloodGroup || '',
        interests: user.interests || []
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      await api.put('/api/users/profile', formData);
      updateUser({ ...user, ...formData });
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      city: user?.city || '',
      state: user?.state || '',
      bloodGroup: user?.bloodGroup || '',
      interests: user?.interests || []
    });
    setIsEditing(false);
  };

  const handleBecomeDonor = async () => {
    if (!user?.bloodGroup) {
      toast.error('Please update your blood group in your profile first');
      return;
    }
    
    setIsLoading(true);
    try {
      await api.post('/api/blood-donors/register', {
        bloodGroup: user.bloodGroup,
        city: user.city,
        state: user.state
      });
      
      // Update user context with new donor status
      updateUser({ ...user, isBloodDonor: true, donorAvailable: true });
      toast.success('Successfully registered as a blood donor!');
    } catch (error) {
      console.error('Donor registration error:', error);
      const errorMsg = error.response?.data?.msg || 'Failed to register as donor';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptOut = async (period = null) => {
    if (isLoading) return; // Prevent multiple rapid clicks
    setIsLoading(true);
    try {
      await api.put('/api/blood-donors/availability', {
        isAvailable: false,
        optOutPeriod: period // in days
      });
      
      // Update user context - keep isBloodDonor true but mark as unavailable
      updateUser({ ...user, isBloodDonor: true, donorAvailable: false });
      toast.success(`Opted out of blood donation${period ? ` for ${period} days` : ''}`);
    } catch (error) {
      console.error('Opt-out error:', error);
      toast.error('Failed to opt out. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptIn = async () => {
    if (isLoading) return; // Prevent multiple rapid clicks
    setIsLoading(true);
    try {
      await api.put('/api/blood-donors/availability', {
        isAvailable: true
      });
      
      // Update user context
      updateUser({ ...user, isBloodDonor: true, donorAvailable: true });
      toast.success('Successfully opted back in to blood donation!');
    } catch (error) {
      console.error('Opt-in error:', error);
      toast.error('Failed to opt in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    { label: 'Blood Donations', value: user?.totalDonations || 0, icon: Droplets, color: 'text-green-500' },
    { label: 'Events Attended', value: '0', icon: Calendar, color: 'text-orange-500' },
    { label: 'Volunteer Hours', value: '0', icon: Activity, color: 'text-green-600' },
  ];

  const quickActions = [
    { name: 'Update Profile', icon: Edit, action: () => setIsEditing(true) },
    { name: 'Find Events', icon: Calendar, action: () => {} },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Welcome back, {user?.name}!</h1>
          <p className="text-gray-600 dark:text-gray-400">Here's what's happening with your account</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-2 rounded-full bg-gray-100 dark:bg-gray-700`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <>
            {/* Profile Card */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Profile Information</h2>
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Full Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Blood Group
                          </label>
                          <select
                            name="bloodGroup"
                            value={formData.bloodGroup}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                          >
                            <option value="">Select Blood Group</option>
                            <option value="A+">A+</option>
                            <option value="A-">A-</option>
                            <option value="B+">B+</option>
                            <option value="B-">B-</option>
                            <option value="AB+">AB+</option>
                            <option value="AB-">AB-</option>
                            <option value="O+">O+</option>
                            <option value="O-">O-</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={isLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3">
                          <User className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Full Name</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.name || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Phone className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Phone Number</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.phone || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MapPin className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">City</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.city || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MapPin className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">State</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.state || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Droplets className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Blood Group</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.bloodGroup || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Blood Donor Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow mt-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                      <Heart className="w-5 h-5 mr-2 text-red-500" />
                      Blood Donor Status
                    </h2>
                    {user?.isBloodDonor && (
                      <div className="flex items-center space-x-2">
                        {user?.donorAvailable ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${user?.donorAvailable ? 'text-green-600' : 'text-red-600'}`}>
                          {user?.donorAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {!user?.isBloodDonor ? (
                    <div className="text-center">
                      <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Become a Blood Donor</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Join our blood donor network and help save lives in your community.
                      </p>
                      <button
                        onClick={handleBecomeDonor}
                        disabled={isLoading || !user?.bloodGroup}
                        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Registering...' : 'Register as Donor'}
                      </button>
                      {!user?.bloodGroup && (
                        <p className="text-sm text-red-600 mt-2">Please update your blood group in your profile first</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Droplets className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">Blood Group: {user?.bloodGroup}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Location: {user?.city}, {user?.state}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total Donations</p>
                          <p className="font-medium text-gray-900 dark:text-white">{user?.totalDonations || 0}</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-3">
                        {user?.donorAvailable ? (
                          <>
                            <button
                              onClick={() => handleOptOut(7)}
                              disabled={isLoading}
                              className="flex-1 px-4 py-2 text-orange-600 border border-orange-300 rounded-md hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-50"
                            >
                              <Clock className="w-4 h-4 inline mr-2" />
                              Opt Out (7 days)
                            </button>
                            <button
                              onClick={() => handleOptOut(30)}
                              disabled={isLoading}
                              className="flex-1 px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                            >
                              <Clock className="w-4 h-4 inline mr-2" />
                              Opt Out (30 days)
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleOptIn}
                            disabled={isLoading}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4 inline mr-2" />
                            Opt Back In
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Quick Actions</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={action.action}
                        className="w-full flex items-center space-x-3 p-3 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <action.icon className="w-5 h-5" />
                        <span>{action.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
